// src/pages/PaymentFailed.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";
import { C, FONT } from "../theme";

/**
 * Shown when BOG redirects back with a failed / cancelled payment.
 * Gives the coach a clear bilingual explanation and a safe path to retry
 * the same competition (via sessionStorage written before the BOG redirect).
 */
export default function PaymentFailed() {
  const navigate = useNavigate();

  // Pull retry context written by Register.tsx just before the BOG redirect.
  // Read once on mount; clear immediately so a stale value can't leak into
  // a later, unrelated /payment-failed visit.
  const [retryCompId, setRetryCompId] = useState<string | null>(null);
  const [retryCompName, setRetryCompName] = useState<string | null>(null);
  useEffect(() => {
    try {
      setRetryCompId(sessionStorage.getItem("pendingCompetitionId"));
      setRetryCompName(sessionStorage.getItem("pendingCompetitionName"));
      sessionStorage.removeItem("pendingOrderId");
      sessionStorage.removeItem("pendingCompetitionId");
      sessionStorage.removeItem("pendingCompetitionName");
    } catch {
      // sessionStorage unavailable — fall through to generic dashboard retry.
    }
  }, []);

  const handleRetry = () => {
    if (retryCompId) navigate(`/register/${retryCompId}`);
    else navigate("/dashboard");
  };

  return (
    <div style={s.page}>
      <Navbar />
      <div style={s.centeredWrapper}>
        <div style={s.card}>
          {/* Icon */}
          <div style={s.iconCircle}>
            <span style={s.icon}>✕</span>
          </div>

          {/* Title — bilingual */}
          <div style={s.title}>Payment Not Completed</div>
          <div style={s.titleKa}>გადახდა არ შესრულდა</div>
          <div style={s.subtitle}>
            Your payment was not processed by the bank.
            <br />
            No athletes have been registered and you have not been charged.
            <br />
            <br />
            თქვენი გადახდა არ დამუშავდა ბანკის მიერ.
            <br />
            სპორტსმენები არ დარეგისტრირდნენ და თანხა არ ჩამოგეჭრათ.
          </div>

          {/* What to do — bilingual */}
          <div style={s.infoBox}>
            <div style={s.infoTitle}>What happened? / რა მოხდა?</div>
            <div style={s.infoText}>
              The payment may have been cancelled, declined by your bank, or the
              session timed out. Please try again — your basket may need to be
              rebuilt.
              <br /><br />
              გადახდა შესაძლოა იყოს გაუქმებული, უარყოფილი ბანკის მიერ, ან
              დროის ლიმიტი ამოწურულია. გთხოვთ სცადოთ ხელახლა.
            </div>
          </div>

          {/* Actions */}
          <div style={s.actions}>
            <button style={s.primaryBtn} onClick={handleRetry}>
              {retryCompName
                ? `Retry: ${retryCompName} / ხელახლა ცდა`
                : "Back to Competitions / კონკურსების სია"}
            </button>
            <button
              style={s.secondaryBtn}
              onClick={() => navigate("/dashboard")}
            >
              Dashboard / დაფა
            </button>
          </div>

          {/* Help */}
          <div style={s.help}>
            Need help? / დახმარება?{" "}
            <a href="mailto:taekwondogeo@gmail.com" style={s.helpLink}>
              taekwondogeo@gmail.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    backgroundColor: C.bg,
  },
  centeredWrapper: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "60px 16px",
  },
  card: {
    background: "var(--color-surface)",
    borderRadius: "16px",
    border: "1px solid var(--color-border)",
    boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
    padding: "48px 40px",
    maxWidth: "480px",
    width: "100%",
    textAlign: "center" as const,
  },
  iconCircle: {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    backgroundColor: "var(--color-danger-soft)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
  },
  icon: {
    fontSize: "26px",
    fontWeight: 800,
    color: "#dc2626",
    lineHeight: 1,
  },
  title: {
    fontFamily: FONT?.display ?? "inherit",
    fontSize: "22px",
    fontWeight: 800,
    color: C.text,
    marginBottom: "4px",
    letterSpacing: "0.5px",
  },
  titleKa: {
    fontSize: "16px",
    fontWeight: 700,
    color: C.text2 ?? "#475569",
    marginBottom: "14px",
  },
  subtitle: {
    fontSize: "14px",
    color: C.text2 ?? "#64748b",
    lineHeight: 1.7,
    marginBottom: "24px",
  },
  infoBox: {
    background: C.warningDim,
    border: `1px solid ${C.warningBorder}`,
    borderRadius: "10px",
    padding: "16px 20px",
    textAlign: "left" as const,
    marginBottom: "28px",
  },
  infoTitle: {
    fontSize: "13px",
    fontWeight: 700,
    color: "var(--color-warning)",
    marginBottom: "6px",
  },
  infoText: {
    fontSize: "13px",
    color: "var(--color-warning)",
    lineHeight: 1.7,
  },
  actions: {
    display: "flex",
    flexDirection: "column" as const,
    gap: "10px",
    marginBottom: "20px",
  },
  primaryBtn: {
    backgroundColor: C.red ?? "#da291c",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "13px 24px",
    fontSize: "14px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
  },
  secondaryBtn: {
    backgroundColor: "transparent",
    color: C.text,
    border: `1px solid ${C.border2 ?? "#cbd5e1"}`,
    borderRadius: "8px",
    padding: "13px 24px",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  help: {
    fontSize: "12px",
    color: C.text2 ?? "#64748b",
  },
  helpLink: {
    color: C.red ?? "#da291c",
    textDecoration: "none",
    fontWeight: 600,
  },
};
